import {isUndefined} from 'lodash';
import React, {FC} from 'react';

import {
  ClickEventHandler,
  FrontClickEvent,
  Handler,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {InteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {ApplicationChannelTypeModel} from '../../../models/api/applicationChannelTypeModel';
import {DraftSummaryModel} from '../../../models/api/draftSummaryModel';
import {ExternalChannelTypeModel} from '../../../models/api/externalChannelTypeModel';
import {MessageModel, MessageStatusesEnum, MessageTypesEnum} from '../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../models/api/recipientModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {TeammatesMessageFoldersEnum} from '../../../models/url/models/inboxesUrlModel';
import {LastCommentDraftModel} from '../../../store/data/conversations/conversationCellSelectors';
import {InboxesTeammateMessagesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellsPanelId} from '../../../store/panels/panelStateBase';
import {BlockLink} from '../../common/links/blockLink';
import {CellBase} from './cellBase';
import {CellActivity} from './content/cell/cellActivity';
import {CellAttachmentIcon} from './content/cell/cellAttachmentIcon';
import {CellDate} from './content/cell/cellDate';
import {CellMiddle} from './content/cell/cellMiddle';
import {CellRecipients} from './content/cell/cellRecipients';
import {MessageCellDragSourceContainer} from './messageCellDragSourceContainer';

/*
 * Props.
 */

export type MessageCellClickEventHandler = (messageUid: string, event: FrontClickEvent<HTMLElement>) => void;
export interface MessageCellProps {
  panelId: CellsPanelId;
  parameters: InboxesTeammateMessagesParameters;
  message: MessageModel;
  draft?: DraftSummaryModel;
  conversationSubject?: string;
  recipients: ReadonlyArray<VisualRecipientModel>;
  date: number;
  reminderDate?: number;
  isSelected?: boolean;
  path?: string;
  onClick?: MessageCellClickEventHandler;
  onDoubleClick?: Handler;
  draftComment?: LastCommentDraftModel;
  hasAutoShareDrafts: boolean;
  externalChannelType?: ExternalChannelTypeModel;
  applicationChannelType?: ApplicationChannelTypeModel;
  hasP2QuickActions: boolean;
  hasP2QuickSnooze: boolean;
}

/*
 * Component.
 */

export const MessageCell: FC<MessageCellProps> = (props) => {
  const {message, path, onDoubleClick} = props;
  const {uid} = message;

  const onClick: ClickEventHandler = (event) => {
    if (!props.onClick) {
      return;
    }

    props.onClick(uid, event);
  };

  const cell = (
    <CellBase isSelected={props.isSelected}>
      {renderTopLine(props)}
      {renderMiddleLine(props)}
      {renderBottomLine(props)}
    </CellBase>
  );

  // TODO: Use role=option with aria-selected instead of a button?
  const clickableCell = isUndefined(path) ? (
    <ChromelessButton onClick={onClick} onDoubleClick={onDoubleClick}>
      {cell}
    </ChromelessButton>
  ) : (
    <BlockLink to={path} onClick={onClick} onDoubleClick={onDoubleClick}>
      {cell}
    </BlockLink>
  );

  if (message.type !== MessageTypesEnum.EMAIL) {
    return clickableCell;
  }

  return (
    <InteractionParent interactionId={`message-cell-${message.uid}`}>
      <MessageCellDragSourceContainer
        panelId={props.panelId}
        parameters={props.parameters}
        isSelected={props.isSelected}
        message={message}
      >
        {clickableCell}
      </MessageCellDragSourceContainer>
    </InteractionParent>
  );
};

/*
 * Line by line.
 */

function renderTopLine(props: MessageCellProps) {
  const {message, recipients, date, isSelected, reminderDate, hasP2QuickActions, hasP2QuickSnooze} = props;
  const receivingRecipients = recipients.filter((r) => r.role !== RecipientRolesEnum.FROM);
  const hasAttachments = message.attachments.length > 0;
  const shouldShowAttachments = !hasP2QuickActions && hasAttachments;
  const dateText = message.status === MessageStatusesEnum.DRAFT_SENDING ? '' : undefined;

  return (
    <>
      <CellRecipients recipients={receivingRecipients} isSelected={isSelected} />
      <CellDate
        date={date}
        isSelected={isSelected}
        reminderDate={reminderDate}
        text={dateText}
        hasP2QuickActions={hasP2QuickActions}
        hasP2QuickSnooze={hasP2QuickSnooze}
      />
      {shouldShowAttachments && <CellAttachmentIcon isSelected={isSelected} />}
    </>
  );
}

function renderMiddleLine(props: MessageCellProps) {
  const {message, conversationSubject, recipients, isSelected, externalChannelType, applicationChannelType} =
    props;
  const recipient = recipients.find((r) => r.role === RecipientRolesEnum.TO);
  const messageSubject = message.subject;

  return (
    <CellMiddle
      messageType={message.type}
      subject={messageSubject || conversationSubject || ''}
      isSelected={isSelected}
      recipient={recipient}
      tags={[]}
      badgeValue={0}
      externalChannelType={externalChannelType}
      applicationChannelType={applicationChannelType}
      hasP2QuickActions={props.hasP2QuickActions}
    />
  );
}

function renderBottomLine(props: MessageCellProps) {
  const {message, isSelected, hasP2QuickActions} = props;
  const hasAttachments = message.attachments.length > 0;
  const shouldShowAttachments = hasP2QuickActions && hasAttachments;

  return (
    <>
      <CellActivity
        conversationId={message.conversationId}
        summary={message.blurb}
        isSelected={isSelected}
        lastCommentDraft={props.draftComment}
        lastComment={undefined}
        lastManualMessage={undefined}
        commentDraftSummary={undefined}
        draft={props.draft}
        hasAutoShareDrafts={props.hasAutoShareDrafts}
        isInDraftsFolder={props.parameters.folder === TeammatesMessageFoldersEnum.DRAFTS}
        useEmptyDraft
      />
      {shouldShowAttachments && (
        <CellAttachmentIcon isSelected={isSelected} hasP2QuickActions={hasP2QuickActions} />
      )}
    </>
  );
}
